import type { SubscriptionDeepLinkAction } from "@/lib/subscriptionActionDeepLink";
import { Info, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { formatSubscriptionDateForDisplay } from "./formatters";
import type { UnavailableReason } from "./subscriptionEligibility";

export interface SubscriptionActionUnavailableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: SubscriptionDeepLinkAction;
  reason: UnavailableReason;
  productTitle?: string | null;
  /** ISO date string used by reasons that mention a date (e.g. paused-until). */
  date?: string | null;
}

function actionLabel(action: SubscriptionDeepLinkAction): string {
  return action === "skip-next-order" ? "skip" : "pause";
}

function actionTitle(action: SubscriptionDeepLinkAction): string {
  return action === "skip-next-order"
    ? "Can't skip this order"
    : "Can't pause this subscription";
}

function messageFor(
  action: SubscriptionDeepLinkAction,
  reason: UnavailableReason,
  productTitle?: string | null,
  date?: string | null,
): string {
  const name = productTitle?.trim() || "this subscription";
  const formattedDate = date ? formatSubscriptionDateForDisplay(date) : null;
  switch (reason) {
    case "no-active-subscriptions":
      return `You don't have any active subscriptions to ${actionLabel(action)}.`;
    case "subscription-not-found":
      return "We couldn't find that subscription on your account.";
    case "already-paused":
      return formattedDate
        ? `${name} is already paused until ${formattedDate}.`
        : `${name} is already paused.`;
    case "not-paused":
      return `${name} isn't paused right now, so there's nothing to resume.`;
    case "no-skips-left":
      return action === "skip-next-order"
        ? `You can't skip another order on ${name} right now.`
        : `${name} can't be paused right now.`;
    case "flexible-plan":
      return `${name} is on a flexible plan, which doesn't support ${actionLabel(action)}.`;
    case "cancelled":
      return `${name} has been cancelled.`;
    case "cancel-scheduled":
      return "This subscription ends after its next delivery. Keep your subscription to make this change.";
    case "cancellation-requested":
      return `${name} has a pending cancellation, so it can't be ${
        action === "skip-next-order" ? "skipped" : "paused"
      } right now.`;
  }
}

export function SubscriptionActionUnavailableDialog({
  open,
  onOpenChange,
  action,
  reason,
  productTitle,
  date,
}: SubscriptionActionUnavailableDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left tw:flex tw:items-center tw:gap-2"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              <Info className="tw:size-5 tw:shrink-0" />
              {actionTitle(action)}
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
        </DialogHeader>

        <p
          className="tw:text-sm tw:my-2"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          {messageFor(action, reason, productTitle, date)}
        </p>

        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="tw:w-full tw:rounded-md tw:bg-[#523D33] tw:border-[#523D33] tw:text-[#FAC8CA] hover:tw:bg-[#523D33]/90 tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px]"
          >
            View my subscriptions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
